import { useCallback, useRef, useState } from "react";
import { DataGrid } from "devextreme-react";
import {
  Column,
  ColumnChooser,
  Export,
  FilterRow,
  Grouping,
  GroupPanel,
  Paging,
  SearchPanel,
  Sorting,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { ScrollView } from "devextreme-react/scroll-view";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";

import { PageProps } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { JeExportByPostingDateModel } from "./model/jeExport";
import { TessituraTransactionJournalEntry } from "./model/modelData";

type ReviewTransactionLevelJournalEntriesProps = {
  pageProps: PageProps;
  jeExport: JeExportByPostingDateModel;
};

const buildDimensionValuesContent = (
  mappingDimensionValues?: Record<string, string>,
  alternativeDimensionValues?: Record<string, string>,
): string => {
  if (!mappingDimensionValues) {
    return "N/A";
  }

  const keys = Object.keys(mappingDimensionValues);
  const labelWidth = keys.reduce((max, key) => Math.max(max, key.length), 0);

  return keys
    .map((key) => {
      const mappingValue = mappingDimensionValues[key] ?? "";
      const hasAlternative =
        alternativeDimensionValues != null &&
        Object.prototype.hasOwnProperty.call(alternativeDimensionValues, key);
      const alternativeText = hasAlternative
        ? `   (Alternative: ${alternativeDimensionValues?.[key] ?? ""})`
        : "";
      return `${key.padEnd(labelWidth)} : ${mappingValue}${alternativeText}`;
    })
    .join("\n");
};

const buildMappingRulesContent = (
  mappingRules?: Record<string, string>,
): string => {
  if (!mappingRules) {
    return "N/A";
  }

  const keys = Object.keys(mappingRules);
  if (keys.length === 0) {
    return "N/A";
  }
  const labelWidth = keys.reduce((max, key) => Math.max(max, key.length), 0);

  return keys
    .map((key) => `${key.padEnd(labelWidth)} : ${mappingRules[key] ?? ""}`)
    .join("\n");
};

export const ReviewTransactionLevelJournalEntries = (
  props: ReviewTransactionLevelJournalEntriesProps,
) => {
  const entries = props.jeExport.transactionJournalEntries;
  const errors = props.jeExport.transactionErrors;

  const dataGridRef = useRef<DataGrid>(null);

  const applyIdFilter = useCallback((value: string) => {
    const grid = dataGridRef.current?.instance;
    if (!grid) {
      return;
    }

    const tokens = value
      .split(",")
      .map((token) => token.trim())
      .filter((token) => token.length > 0);
    if (tokens.length === 0) {
      grid.filter(null);
      return;
    }

    const selector = (rowData: TessituraTransactionJournalEntry) => {
      const transaction = (rowData.tessituraTransaction as any)?.transaction;
      return `${transaction?.no ?? ""} ${transaction?.sequenceNo ?? ""}`;
    };
    const conditions = tokens.map((token) => [selector, "contains", token]);
    if (conditions.length === 1) {
      grid.filter(conditions[0]);
      return;
    }
    const filterExpr: any[] = [];
    conditions.forEach((condition, index) => {
      if (index > 0) {
        filterExpr.push("or");
      }
      filterExpr.push(condition);
    });
    grid.filter(filterExpr);
  }, []);

  const handleToolbarPreparing = useCallback(
    (e: any) => {
      const items = e.toolbarOptions.items;
      const searchPanelIndex = items.findIndex(
        (item: any) => item.name === "searchPanel",
      );
      const idSearchItem = {
        location: "after",
        locateInMenu: "never",
        widget: "dxTextBox",
        options: {
          mode: "search",
          placeholder: "Transaction No / Sequence No (comma separated)",
          width: 300,
          showClearButton: true,
          valueChangeEvent: "keyup",
          onValueChanged: (args: { value?: string }) =>
            applyIdFilter(args.value ?? ""),
        },
      };
      if (searchPanelIndex >= 0) {
        items.splice(searchPanelIndex, 0, idSearchItem);
      } else {
        items.push(idSearchItem);
      }
    },
    [applyIdFilter],
  );

  const [popupVisible, setPopupVisible] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupContent, setPopupContent] = useState("");

  const showPopup = useCallback((title: string, content: string) => {
    setPopupTitle(title);
    setPopupContent(content || "N/A");
    setPopupVisible(true);
  }, []);

  const hidePopup = useCallback(() => {
    setPopupVisible(false);
  }, []);

  const copyPopupContent = useCallback(() => {
    void navigator.clipboard.writeText(popupContent);
  }, [popupContent]);

  const handleExporting = useCallback((e: any) => {
    const postingDate = props.jeExport.postingDate
      ? new Date(props.jeExport.postingDate)
      : undefined;
    const postingDateSuffix = postingDate
      ? `${postingDate.getFullYear()}-${String(postingDate.getMonth() + 1).padStart(2, "0")}-${String(postingDate.getDate()).padStart(2, "0")}`
      : "";
    const fileName = `RawJournalEntries${postingDateSuffix}.xlsx`;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Raw Journal Entries");

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      selectedRowsOnly: e.selectedRowsOnly,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer as any], { type: "application/octet-stream" }),
          fileName,
        );
      });
    });

    e.cancel = true;
  }, [props.jeExport.postingDate]);

  const renderExceptionButton = useCallback((cellInfo: { data: { exception?: string } }) => {
    return (
      <Button
        text="View"
        type="default"
        stylingMode="text"
        disabled={!cellInfo.data.exception}
        onClick={() => showPopup("Exception", cellInfo.data.exception || "")}
      />
    );
  }, [showPopup]);

  const renderStackTraceButton = useCallback((cellInfo: { data: { stackTrace?: string } }) => {
    return (
      <Button
        text="View"
        type="default"
        stylingMode="text"
        disabled={!cellInfo.data.stackTrace}
        onClick={() => showPopup("Stack Trace", cellInfo.data.stackTrace || "")}
      />
    );
  }, [showPopup]);

  const renderTransactionInfoButton = useCallback(
    (cellInfo: { data: TessituraTransactionJournalEntry }) => {
      const txn = cellInfo.data.tessituraTransaction;
      return (
        <Button
          text="View"
          type="default"
          stylingMode="text"
          disabled={!txn}
          onClick={() =>
            showPopup("Transaction Info", JSON.stringify(txn, null, 2))
          }
        />
      );
    },
    [showPopup],
  );

  const renderMappingDimensionValuesButton = useCallback(
    (cellInfo: { data: TessituraTransactionJournalEntry }) => {
      const mappingDimensionValues = cellInfo.data.mappingDimensionValues;
      const alternativeDimensionValues = cellInfo.data.alternativeDimensionValues;
      return (
        <Button
          text="View"
          type="default"
          stylingMode="text"
          disabled={!mappingDimensionValues}
          onClick={() =>
            showPopup(
              "Mapping Dimension Values",
              buildDimensionValuesContent(
                mappingDimensionValues,
                alternativeDimensionValues,
              ),
            )
          }
        />
      );
    },
    [showPopup],
  );

  const renderMappingRulesButton = useCallback(
    (cellInfo: { data: TessituraTransactionJournalEntry }) => {
      const mappingRules = cellInfo.data.mappingRules;
      return (
        <Button
          text="View"
          type="default"
          stylingMode="text"
          disabled={!mappingRules}
          onClick={() =>
            showPopup("Mapping Rules", buildMappingRulesContent(mappingRules))
          }
        />
      );
    },
    [showPopup],
  );

  return (
    <>
      {errors && errors.length > 0 && (
        <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="transaction-errors-content"
            id="transaction-errors-header"
          >
            <Typography variant="h5">
              {props.pageProps.appUtil.getLocaleString("transactionErrors")} ({errors.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DataGrid
              dataSource={errors}
              height={300}
              showBorders={true}
              allowColumnResizing={true}
              columnResizingMode="widget"
              columnMinWidth={50}
              columnAutoWidth={true}
            >
              <Paging defaultPageSize={10} defaultPageIndex={0} />
              <Column dataField="message" caption="Error Message" />
              <Column caption="Exception Detail" width={150} alignment="center" cellRender={renderExceptionButton} />
              <Column caption="Stack Trace" width={120} alignment="center" cellRender={renderStackTraceButton} />
            </DataGrid>
          </AccordionDetails>
        </Accordion>
      )}
      <MainCard title={props.pageProps.appUtil.getLocaleString("transactionJournalEntries")}>
        <DataGrid
          ref={dataGridRef}
          dataSource={entries}
          height={600}
          showBorders={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
          columnMinWidth={50}
          columnAutoWidth={true}
          onExporting={handleExporting}
          onToolbarPreparing={handleToolbarPreparing}
        >
          <Paging defaultPageSize={10} defaultPageIndex={0} />
          <GroupPanel visible={true} />
          <Grouping autoExpandAll={false} />
          <Export enabled={true} allowExportSelectedData={true} />
          <SearchPanel
            visible={true}
            highlightCaseSensitive={true}
            placeholder={props.pageProps.appUtil.getLocaleString("search")}
          />
          <ColumnChooser enabled={true} />
          <Sorting mode="multiple" />
          <FilterRow visible={true} />
          <Column dataField="accountingDate" caption="Accounting Date" dataType="date" />
          <Column dataField="fiscalYear" caption="Fiscal Year" />
          <Column dataField="accountingPeriod" caption="Accounting Period" />
          <Column dataField="accountNo" caption="Account No" />
          <Column dataField="prefix" caption="Prefix" />
          <Column dataField="suffix" caption="Suffix" />
          <Column dataField="type" caption="Type" />
          <Column dataField="amount" caption="Amount" dataType="number" format="#,##0.00" />
          <Column caption="Transaction Info" width={120} alignment="center" cellRender={renderTransactionInfoButton} />
          <Column caption="Mapping Dimension Values" width={160} alignment="center" cellRender={renderMappingDimensionValuesButton} />
          <Column caption="Mapping Rules" width={160} alignment="center" cellRender={renderMappingRulesButton} />
          <Column dataField="isPayment" caption="Is Payment" dataType="boolean" />
          <Column dataField="referenceField" caption="Reference Field" />
          <Column dataField="referenceStringField" caption="Reference String" />
        </DataGrid>
      </MainCard>
      <Popup
        visible={popupVisible}
        onHiding={hidePopup}
        title={popupTitle}
        showCloseButton={true}
        width="60%"
        height="80%"
      >
        <ToolbarItem
          toolbar="top"
          location="after"
          widget="dxButton"
          options={{
            icon: "copy",
            hint: "Copy",
            stylingMode: "text",
            onClick: copyPopupContent,
          }}
        />
        <ScrollView width="100%" height="100%">
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            {popupContent}
          </pre>
        </ScrollView>
      </Popup>
    </>
  );
};
